import { CustomFieldsMapper, GPSTrackMapper, HexColor, LocationMapper } from '@nx-smartmonkey/shared/domain';
import { Driver } from '../../domain/driver/Driver';
import { DriverDTO } from '../../domain/driver/DriverDTO';
import { DriverRepositoryDTO } from '../../domain/driver/DriverRepositoryDTO';
import { ProjectCustomFields } from '../../domain/project/ProjectCustomFields';
import { DriversSurveyMapper } from '../driversSurvey/DriversSurveyMapper';
import { StopMapper } from '../stop/StopMapper';

export class DriverMapper {
  static toDomain(props: DriverDTO, projectCustomFields: ProjectCustomFields): Driver {
    const {
      id,
      plan_id,
      project_id,
      external_id,
      vehicle_id,
      label,
      phone,
      comments,
      email,
      plate,
      color,
      vehicle_model,
      brand,
      time_window,
      geo_fences,
      max_distance,
      min_distance,
      max_time,
      min_time,
      max_weight,
      max_volume,
      max_services,
      start_location,
      end_location,
      provides,
      start_date,
      end_date,
      status,
      is_locked,
      planned_track,
      planned_start_time,
      planned_end_time,
      total_distance,
      distance_traveled,
      last_position,
      price_per_distance,
      price_per_minute,
      tracing_percent,
      custom_fields,
      stops,
      survey,
      deleted,
      created_by,
      created_at,
      updated_at,
      deleted_at,
    } = props;

    const startLocationDomain = start_location ? LocationMapper.toDomain(start_location) : undefined;
    const endLocationDomain = end_location ? LocationMapper.toDomain(end_location) : undefined;

    const lastPositionDomain = last_position ? GPSTrackMapper.toDomain(last_position) : undefined;

    return Driver.create(
      {
        plan_id,
        project_id,
        external_id,
        vehicle_id,
        label,
        phone,
        comments,
        email,
        plate,
        color: color ? HexColor.create(color) : undefined,
        vehicle_model,
        brand,
        time_window,
        geo_fences,
        max_distance,
        min_distance,
        max_time,
        min_time,
        max_weight,
        max_volume,
        max_services,
        start_location: startLocationDomain,
        end_location: endLocationDomain,
        provides,
        start_date,
        end_date,
        status,
        is_locked,
        planned_track,
        planned_start_time,
        planned_end_time,
        total_distance,
        distance_traveled,
        last_position: lastPositionDomain,
        price_per_distance,
        price_per_minute,
        tracing_percent,
        custom_fields: custom_fields
          ? CustomFieldsMapper.toDomain(custom_fields, projectCustomFields.vehicle)
          : undefined,
        stops: stops ? stops.map((stop) => StopMapper.toDomain(stop, projectCustomFields)) : [],
        survey: survey ? DriversSurveyMapper.toDomain(survey) : undefined,
        deleted,
        created_by,
        created_at,
        updated_at,
        deleted_at,
      },
      id
    );
  }

  static toDTO(driver: Driver): DriverDTO {
    return {
      id: driver.id,
      plan_id: driver.plan_id,
      project_id: driver.project_id,
      external_id: driver.external_id,
      vehicle_id: driver.vehicle_id,
      label: driver.label,
      phone: driver.phone,
      comments: driver.comments,
      email: driver.email,
      plate: driver.plate,
      color: driver.color ? driver.color.value : undefined,
      vehicle_model: driver.vehicle_model,
      brand: driver.brand,
      time_window: driver.time_window,
      geo_fences: driver.geo_fences,
      max_distance: driver.max_distance,
      min_distance: driver.min_distance,
      max_time: driver.max_time,
      min_time: driver.min_time,
      max_weight: driver.max_weight,
      max_volume: driver.max_volume,
      max_services: driver.max_services,
      start_location: driver.start_location ? LocationMapper.toDTO(driver.start_location) : undefined,
      end_location: driver.end_location ? LocationMapper.toDTO(driver.end_location) : undefined,
      provides: driver.provides,
      start_date: driver.start_date,
      end_date: driver.end_date,
      status: driver.status,
      is_locked: driver.is_locked,
      planned_track: driver.planned_track,
      planned_start_time: driver.planned_start_time,
      planned_end_time: driver.planned_end_time,
      total_distance: driver.total_distance,
      distance_traveled: driver.distance_traveled,
      last_position: driver.last_position ? GPSTrackMapper.toDTO(driver.last_position) : undefined,
      price_per_distance: driver.price_per_distance,
      price_per_minute: driver.price_per_minute,
      tracing_percent: driver.tracing_percent,
      custom_fields: driver.custom_fields,
      stops: driver.stops.map(StopMapper.toDTO),
      survey: driver.survey ? DriversSurveyMapper.toDTO(driver.survey) : undefined,
      deleted: driver.deleted,
      created_by: driver.created_by,
      created_at: driver.created_at,
      updated_at: driver.updated_at,
      deleted_at: driver.deleted_at,
    };
  }

  static toRepository(driver: Driver): DriverRepositoryDTO {
    return {
      external_id: driver.external_id,
      vehicle_id: driver.vehicle_id,
      label: driver.label,
      phone: driver.phone,
      comments: driver.comments,
      email: driver.email,
      plate: driver.plate,
      color: driver.color?.value,
      vehicle_model: driver.vehicle_model,
      brand: driver.brand,
      time_window: driver.time_window,
      geo_fences: driver.geo_fences,
      max_distance: driver.max_distance,
      min_distance: driver.min_distance,
      max_time: driver.max_time,
      min_time: driver.min_time,
      max_weight: driver.max_weight,
      max_volume: driver.max_volume,
      max_services: driver.max_services,
      ...(driver.start_location !== undefined
        ? { start_location: driver.start_location !== null ? LocationMapper.toDTO(driver.start_location) : null }
        : {}),
      ...(driver.end_location !== undefined
        ? { end_location: driver.end_location !== null ? LocationMapper.toDTO(driver.end_location) : null }
        : {}),
      provides: driver.provides,
      status: driver.status,
      is_locked: driver.is_locked,
      price_per_distance: driver.price_per_distance,
      price_per_minute: driver.price_per_minute,
      custom_fields: driver.custom_fields ? CustomFieldsMapper.toRepository(driver.custom_fields) : undefined,
      stops: driver.stops.map((stop) => StopMapper.toRepository(stop)),
    };
  }
}
